import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  ORDER_FEATURES,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectAdjustmentSettings, selectCompSequence } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectDepreciationRatiosForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';
import { applyRoundingAndThresholds } from './utils/applyRoundingAndThresholds.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateBarnAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatios: ReturnType<typeof selectDepreciationRatiosForFeature>,
  compID: string,
) => {
  const subjectImprovements = improvements.subject;
  const compImprovements = improvements[compID];

  const subjectPropertyIdentification = propertyIdentification.subject;
  const compPropertyIdentification = propertyIdentification[compID];

  if (
    isUndefined(subjectImprovements) ||
    isUndefined(compImprovements) ||
    isUndefined(depreciationRatios) ||
    isUndefined(subjectPropertyIdentification) ||
    isUndefined(compPropertyIdentification)
  ) {
    return undefined;
  }

  const depreciationRatio = depreciationRatios.subject;
  const compDepreciationRatio = depreciationRatios[compID];

  if (isUndefined(depreciationRatio) || isUndefined(compDepreciationRatio)) {
    return undefined;
  }

  const { barn } = subjectImprovements;
  const { barn: compBarn } = compImprovements;
  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;
  const {
    address: { PostalCode: compZipCode },
  } = compPropertyIdentification;

  if (isUndefined(zipCode) || isUndefined(compZipCode)) {
    return undefined;
  }

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  return adjCalculator.getBarnAdj({
    subject: {
      hasFeature: ORDER_FEATURES.barn.hasFeature(subjectImprovements),
      sqft: barn?.area ? AloftArea.schema.parse(barn?.area) : undefined,
      quality: barn?.quality,
      zipCode,
    },
    comp: {
      hasFeature: ORDER_FEATURES.barn.hasFeature(compImprovements),
      sqft: compBarn?.area ? AloftArea.schema.parse(compBarn?.area) : undefined,
      quality: compBarn?.quality,
      zipCode: compZipCode,
    },
    depreciationRatio,
    compDepreciationRatio,
  }).adjustment;
};

const selectBarnAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'barn'),
    (state, id: string) => id,
  ],
  calculateBarnAdj,
);

export const selectBarnAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'barn'),
    selectCompSequence,
  ],
  (adjustmentProps, improvements, propertyIdentification, depreciationRatios, compSequence) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateBarnAdj(
        adjustmentProps,
        improvements,
        propertyIdentification,
        depreciationRatios,
        propID,
      );
      if (isDefined(adj)) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedBarnAdj = createPriceSelector(
  [selectBarnAdj, selectAdjustmentSettings],
  (adj, adjSettings) => {
    if (isUndefined(adj)) {
      return undefined;
    }

    return applyRoundingAndThresholds(adj, 'barn', adjSettings);
  },
);

export const selectAppliedBarnAdjDisplay = createSelector([selectAppliedBarnAdj], (adj) => {
  if (isUndefined(adj)) {
    return undefined;
  }

  return {
    value: getStringValueFromAdjustment(adj),
    color: getColorFromAdjustment(adj),
  };
});

export const selectAppliedBarnAdjsDisplay = createSelector(
  [selectBarnAdjs, selectAdjustmentSettings, selectManualAdjustmentsEntities],
  (adjs, settings, manualAdjustmentsEntities) =>
    Object.keys(adjs).reduce((acc: Record<string, { value: string; color: string }>, propID) => {
      const adj = adjs[propID];
      const manualAdjustment = manualAdjustmentsEntities[propID]?.barn;
      if (isDefined(manualAdjustment)) {
        const manualAdjustmentPrice = AloftPrice.schema.parse(manualAdjustment);
        acc[propID] = {
          value: `${getStringValueFromAdjustment(manualAdjustmentPrice)}*`,
          color: getColorFromAdjustment(manualAdjustmentPrice),
        };
      } else if (isDefined(adj)) {
        acc[propID] = {
          value: getStringValueFromAdjustment(applyRoundingAndThresholds(adj, 'barn', settings)),
          color: getColorFromAdjustment(adj),
        };
      }
      return acc;
    }, {}),
);
